import FormaFarmaceuticaModel, { FormaFarmaceuticaListParameters } from "@/models/formaFarmaceuticaModel";
import PaginationModel from "@/models/paginationModel";
import arithmeticHelper from "@/utils/common/arithmeticHelper";
import { getFormaFarmaceuticaCombo } from "@/utils/common/combo/combotext";
import { getCustomParams, getPaginationParams } from "@/utils/common/http";
import nfetch from "@/utils/events/nfetch";

import { iServiceCached } from "./base/iServiceCached";

class FormaFarmaceuticaService extends iServiceCached<FormaFarmaceuticaModel> {
    constructor() {
        super("FormaFarmaceutica");
    }

    public getByFranquia(id: number, franquiaId: number): Promise<Response> {
        //@ts-ignore
        return nfetch(`/${this.controllerName}/GetByFranquia?id=${id}&franquiaId=${franquiaId}`, {
            credentials: "same-origin",
        });
    }

    public listFormaFarmaceutica(
        filterKey: string,
        sortKey: string,
        sortOrder: string,
        pageIndex: number,
        pageSize: number,
        customProperties: { [key: string]: any } = {},
    ) {
        const custom = getCustomParams(customProperties);
        const pagination = getPaginationParams(filterKey, sortKey, sortOrder, pageIndex, pageSize);

        const retorno = nfetch(`/${this.controllerName}/ListFormaFarmaceutica?${pagination}` + custom, {
            credentials: "same-origin",
        });

        return retorno.withLoading().resolveWithJSON<PaginationModel<FormaFarmaceuticaModel>>();
    }

    public async comboMethod(franquiaId?: number) {
        const customParameters: FormaFarmaceuticaListParameters = {
            somenteAtivos: false,
        };

        if (franquiaId) {
            customParameters.franquiaId = franquiaId;
        }

        const response = await this.listFormaFarmaceutica("", "descricao", "asc", 1, 999999, customParameters);

        return response.list;
    }

    public async comboCached(franquiaId: number, somenteAtivos = true, refresh = false) {
        const retorno = await this.combo(franquiaId, refresh);

        return retorno
            .filter(p => (somenteAtivos ? p.ativo == somenteAtivos : true))
            .map(item => ({ ...item, ...getFormaFarmaceuticaCombo(item) }));
    }

    public replicar(id: number, idFranquias: string, atualizarValores: boolean): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/Replicar?id=${id}&idFranquias=${idFranquias}&atualizarValores=${atualizarValores}`,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
            },
        );
    }

    public getByParent(parentId: number, franquiaId: number) {
        const retorno = nfetch(`/${this.controllerName}/GetByParentId?parentId=${parentId}&franquiaId=${franquiaId}`, {
            credentials: "same-origin",
        });

        return retorno.withLoading().resolveWithJSON<FormaFarmaceuticaModel>();
    }

    public getValorFormaFarmaceutica(
        valorSoma: number,
        quantidade: number,
        fatorMultiplicadorAdicional: number,
        fatorDecaimento: number,
        fatorCompensacao: number,
    ) {
        let fatorCurva = 0;
        fatorMultiplicadorAdicional = fatorMultiplicadorAdicional ? fatorMultiplicadorAdicional : 0;
        fatorDecaimento = fatorDecaimento ? fatorDecaimento : 0;
        fatorCompensacao = fatorCompensacao ? fatorCompensacao : 0;
        const fatorMultMinimo = fatorMultiplicadorAdicional * quantidade;
        if (fatorDecaimento > 0 && fatorCompensacao > 0) {
            //verifica o ponto limite do decaimento
            let limitQuantidadeAd = arithmeticHelper.round(fatorCompensacao / fatorDecaimento, 0);
            if (limitQuantidadeAd > quantidade) {
                limitQuantidadeAd = quantidade;
            }

            //soma das compensacoes - soma dos decaimentos
            const fatorAdicional = fatorCompensacao * limitQuantidadeAd;
            //formula de PA
            const fatorDecaimentoAdicional = (fatorDecaimento * (1 + limitQuantidadeAd) * limitQuantidadeAd) / 2;
            fatorCurva = fatorAdicional - fatorDecaimentoAdicional;
        }
        return arithmeticHelper.round(valorSoma + (fatorMultMinimo + fatorCurva));
    }

    public comboFranquia(franquiaId: number): Promise<Response> {
        return nfetch(`/${this.controllerName}/ListFormaFarmaceutica?franquiaId=${franquiaId}`, {
            credentials: "same-origin",
        });
    }
}

export default new FormaFarmaceuticaService();
